import type { Plan } from "@circle-react/types";

import { type PlanActions, planActions } from "@/react/helpers/plansHelpers";

import { type ButtonConfig, useButtonConfig } from "./useButtonConfig";
import { usePlanActions } from "./usePlanActions";

type ActionHandler = () => void | Promise<void>;

interface SubmitButtonConfig extends ButtonConfig {
  handler: ActionHandler;
  plan: Plan;
}

export const useSubmitButton = (plan: Plan): SubmitButtonConfig => {
  const buttonConfig = useButtonConfig(plan);
  const actions = usePlanActions(plan);

  const actionHandlers: Record<PlanActions, ActionHandler> = {
    [planActions.upgrade]: actions.handleChangePlanButtonClick,
    [planActions.change_plan]: actions.handleChangePlanButtonClick,
    [planActions.downgrade]: actions.handleChangePlanButtonClick,
    [planActions.resume]: actions.handleResumeButtonClick,
    [planActions.renew]: actions.handleRenewButtonClick,
    [planActions.current_plan]: actions.handleCurrentPlanButtonClick,
    [planActions.redeem_offer]: actions.handleRedeemOfferButtonClick,
    [planActions.contact_support]: actions.handleContactSupportOnClick,
  };

  const getButtonClickHandler = (buttonString: PlanActions): ActionHandler => {
    const handler = actionHandlers[buttonString];
    if (!handler) {
      throw new Error(`No handler found for plan action: ${buttonString}`);
    }
    return handler;
  };

  const handler = buttonConfig.shouldShowContactSupport
    ? actions.handleContactSupportOnClick
    : getButtonClickHandler(plan.plan_action);

  return {
    ...buttonConfig,
    handler,
    plan,
  };
};
